// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkHintColor, chunkSelectedColor } from "components/shell/presentation/AppStyles";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { ColorComponent, DefaultControlColorComponent } from "sdv/components/ColorComponent";
import { RenderBox, ChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { clampAndCreateChunkBounds, mergeBoxesIntoContainerBoxes, sortEntitiesByTokenRangeAscending } from "sdv/utils/TokenBounds";
import { Box, boxesIntersectX, distanceFromBoxSquared, pointInBox } from "store/shared_types";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { ResizeComponent, TokenSnapBehavior } from "sdv/components/ResizeComponent";
import { clamp } from "utils/MathUtils";
import { OutflowRenderComponent } from "sdv/components/OutflowRenderComponent";

/**
 * Prepares chunk selections for rendering and handles resize interaction visuals for chunk bounds
 */
export class ChunkRenderSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;
    queries: Queries = {
        chunks: { types: [ComponentId.ChunkRender, ComponentId.TokenRange, ComponentId.TokenRangeInfo, ComponentId.Renderable] },
        resizeChunks: { types: [ComponentId.ChunkRender, ComponentId.Resize, ComponentId.TokenRangeInfo, ComponentId.TokenRange, ComponentId.Renderable] },
        tables: { types: [ComponentId.Table, ComponentId.Bounds] },
        outflows: { types: [ComponentId.Outflow, ComponentId.Renderable] }
    }

    update() {
        this.updateChunks();
        this.renderResize();
    }

    private updateChunks() {
        let sortedStructuralEntities: Entity[] | null = null;
        for (const chunk of this.queries.chunks.query!.entities) {
            if (chunk.hasComponent(ComponentId.Resize)) {
                continue;
            }

            const boundsComp = chunk.getComponent<BoundsComponent>(ComponentId.Bounds);
            let bounds = boundsComp?.bounds;
            if (!bounds) {
                // No bounds component - that means this isn't a "Control" from the DGML so we need to generate the bounds
                sortedStructuralEntities = sortedStructuralEntities ?? sortEntitiesByTokenRangeAscending([...this.queries.tables.query!.entities])
                const tokenRange = chunk.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
                if (!TokenRangeUtils.rangeIsEmpty(tokenRange)) {
                    bounds = clampAndCreateChunkBounds(tokenRange.fromTok, tokenRange.toTok, this.resources.get("pageTokens").value, this.resources.get("visiblePages").value, 10, sortedStructuralEntities);
                }
            }

            if (!bounds) {
                continue;
            }

            const renderInfo = chunk.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
            const colorComp = chunk.getComponent<ColorComponent>(ComponentId.Color) ?? DefaultControlColorComponent;

            const getColoredBox = (box: Box) => {
                return { box: box, color: colorComp.color }
            }

            if (renderInfo.bounds !== bounds) {
                renderInfo.bounds = bounds;
                renderInfo.containerBoxes = boundsComp?.containerBoxes ?? mergeBoxesIntoContainerBoxes(renderInfo.bounds);
                const indented = ChunkRenderSystem.indentContainerBoxes(renderInfo.bounds, renderInfo.containerBoxes);
                renderInfo.renderBoxes = indented.map(b => getColoredBox(b));
            }
            else {
                renderInfo.renderBoxes.forEach(x => x.color = colorComp.color);
            }
        }

        for (const chunk of this.queries.outflows.query!.entities) {
            if (chunk.hasComponent(ComponentId.Resize)) {
                continue;
            }

            const boundsComp = chunk.getComponent<BoundsComponent>(ComponentId.Bounds);
            if (!boundsComp) {
                continue;
            }

            const renderInfo = chunk.getComponent<OutflowRenderComponent>(ComponentId.OutflowRender)!;
            const colorComp = chunk.getComponent<ColorComponent>(ComponentId.Color) ?? DefaultControlColorComponent;

            const getColoredBox = (box: Box) => {
                return { box: box, color: colorComp.color }
            }

            if (renderInfo.bounds !== boundsComp.bounds) {
                renderInfo.bounds = boundsComp.bounds;
                renderInfo.containerBoxes = boundsComp.containerBoxes ?? mergeBoxesIntoContainerBoxes(renderInfo.bounds);
                renderInfo.renderBoxes = renderInfo.containerBoxes.map(b => getColoredBox(b));
            }
            else {
                renderInfo.renderBoxes.forEach(x => x.color = colorComp.color);
            }
        }
    }

    private renderResize() {
        let sortedStructuralEntities: Entity[] | null = null;
        const cursor = this.input.getAxis2d(ActionId.SelectCursor);
        for (const chunk of this.queries.resizeChunks.query!.entities) {
            const tokenRange = chunk.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
            const renderInfo = chunk.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
            const resizeComp = chunk.getComponent<ResizeComponent>(ComponentId.Resize)!;

            sortedStructuralEntities = sortedStructuralEntities ?? sortEntitiesByTokenRangeAscending([...this.queries.tables.query!.entities])
            const refresh = !renderInfo.lastRange ||
                !TokenRangeUtils.rangesEqual(tokenRange.tokenRange, renderInfo.lastRange) ||
                this.resources.get("visiblePages").updated ||
                (this.resources.get("pagesLoaded").updated && this.resources.get("pagesLoaded").value);

            if (refresh) {
                const bounds = clampAndCreateChunkBounds(
                    tokenRange.tokenRange.fromTok,
                    tokenRange.tokenRange.toTok,
                    this.resources.get("pageTokens").value,
                    this.resources.get("visiblePages").value,
                    10,
                    sortedStructuralEntities);

                renderInfo.bounds = bounds;
                renderInfo.containerBoxes = mergeBoxesIntoContainerBoxes(renderInfo.bounds);
                renderInfo.lastRange = { ...tokenRange.tokenRange };
            }

            if (!!cursor && !!resizeComp.isResizing) {
                const renderBoxes: RenderBox[] = [];
                if (renderInfo.containerBoxes.length > 0) {
                    const primaryColor = resizeComp.primaryColor ?? chunkSelectedColor;
                    const secondaryColor = resizeComp.secondaryColor ?? chunkHintColor;

                    const containerBoxes = ChunkRenderSystem.indentContainerBoxes(renderInfo.bounds, renderInfo.containerBoxes);

                    if (resizeComp.snapBehavior === TokenSnapBehavior.Token) {
                        // Left/right highlighting
                        for (let i = 0; i < containerBoxes.length; ++i) {
                            const box = { ...containerBoxes[i] };
                            if (i === 0 && !!resizeComp.isFromTok) {
                                const hintBox = { ...box };
                                hintBox.right = clamp(cursor.x, box.left, box.right);
                                box.left = clamp(cursor.x, box.left, box.right);
                                renderBoxes.push({ box: hintBox, color: secondaryColor });
                            }
                            renderBoxes.push({ box: box, color: primaryColor });
                            if (i === containerBoxes.length - 1 && !resizeComp.isFromTok) {
                                const hintBox = { ...box };
                                hintBox.left = clamp(cursor.x, box.left, box.right);
                                box.right = clamp(cursor.x, box.left, box.right);
                                renderBoxes.push({ box: hintBox, color: secondaryColor });
                            }
                        }
                    }
                    else {
                        // Top/bottom highlighting
                        const isFromTok = !!resizeComp.isFromTok;
                        const isToTok = !resizeComp.isFromTok;

                        // Find the container box closest to or intersecting the cursor
                        let closestDist = Infinity;
                        let index = -1;
                        for (let i = 0; i < containerBoxes.length; ++i) {
                            const containerBox = containerBoxes[i];
                            if (pointInBox(cursor.x, cursor.y, containerBox)) {
                                index = i;
                                break;
                            }
                            else {
                                const dist = distanceFromBoxSquared(containerBox, cursor.x, cursor.y);
                                if (dist < closestDist) {
                                    closestDist = dist;
                                    index = i;
                                }
                            }
                        }

                        if (index !== -1) {
                            let targetIndex = index;
                            let targetBox = containerBoxes[targetIndex];
                            while (targetIndex >= 0 && targetIndex < containerBoxes.length) {
                                const isIntersect = cursor.y >= targetBox.top && cursor.y <= targetBox.bottom;
                                const isContained = (isFromTok && cursor.y <= targetBox.top) || (isToTok && cursor.y > targetBox.bottom);
                                if (isIntersect || isContained) {
                                    index = targetIndex
                                    targetIndex = isFromTok ? targetIndex - 1 : targetIndex + 1;
                                }
                                else {
                                    break;
                                }

                                targetBox = containerBoxes[targetIndex];
                            }
                        }

                        for (let i = 0; i < containerBoxes.length; ++i) {
                            const curBox = containerBoxes[i];
                            if (i === index && cursor.y >= curBox.top && cursor.y <= curBox.bottom) {
                                // Split
                                const topBox: RenderBox = {
                                    box: {
                                        ...curBox,
                                        bottom: cursor.y
                                    },
                                    color: isToTok ? primaryColor : secondaryColor
                                }

                                const bottomBox: RenderBox = {
                                    box: {
                                        ...curBox,
                                        top: cursor.y
                                    },
                                    color: isFromTok ? primaryColor : secondaryColor
                                }

                                renderBoxes.push(topBox, bottomBox);
                            }
                            else {
                                let color: string;
                                if (isFromTok) {
                                    color = i >= index || (i === 0 && cursor.y < curBox.top) ? primaryColor : secondaryColor;
                                }
                                else {
                                    color = i <= index || (i === containerBoxes.length - 1 && cursor.y > curBox.bottom) ? primaryColor : secondaryColor;
                                }

                                renderBoxes.push({
                                    box: curBox,
                                    color: color
                                })
                            }
                        }
                    }
                }

                renderInfo.renderBoxes = renderBoxes;
            }
            else {
                const colorComp = chunk.getComponent<ColorComponent>(ComponentId.Color) ?? DefaultControlColorComponent;
                const boxes: Box[] = ChunkRenderSystem.indentContainerBoxes(renderInfo.bounds, renderInfo.containerBoxes);
                renderInfo.renderBoxes = boxes.map(x => { return { box: x, color: colorComp.color } });
            }
        }
    }

    private static indentContainerBoxes(tokenBoxes: Box[], containerBoxes: Box[]): Box[] {
        if (!tokenBoxes.length || !containerBoxes.length) {
            return [];
        }

        containerBoxes = containerBoxes.map(x => { return { ...x } });
        if (tokenBoxes.length === 1) {
            return containerBoxes;
        }

        const firstBox = tokenBoxes[0];
        const firstContainerbox = containerBoxes[0];
        const lastBox = tokenBoxes[tokenBoxes.length - 1];
        const lastContainerBox = containerBoxes[containerBoxes.length - 1];

        const indentedBoxes: Box[] = [];
        if (!!firstBox && !!firstContainerbox) {
            // Indent the first line by splitting the first container box and generating a new box
            // based on our first bounds box.
            const newContainerBox: Box = {
                ...firstBox,
                right: firstContainerbox.right,
            }

            firstContainerbox.top = newContainerBox.bottom
            indentedBoxes.push(newContainerBox);

            if (tokenBoxes.length > 2 || boxesIntersectX(firstBox, lastBox)) {
                indentedBoxes.push(...containerBoxes);
            }

            if (firstBox !== lastBox) {
                // Indent the last line by splitting the last container box and generating a new box
                // based on our last bounds box.
                const newContainerBox: Box = {
                    ...lastBox,
                    left: lastContainerBox.left,
                }

                lastContainerBox.bottom = newContainerBox.top;
                indentedBoxes.push(newContainerBox);
            }
        }

        return indentedBoxes;
    }
}
